import * as THREE from 'three'

// Classic 2D Perlin noise — tiling, deterministic
// Generates a 128x128 half-float texture with RepeatWrapping

const SIZE = 128

// Permutation table (doubled for wrapping)
const perm = new Uint8Array(512)
const grad2 = [
  [1, 1], [-1, 1], [1, -1], [-1, -1],
  [1, 0], [-1, 0], [0, 1], [0, -1],
]

// Seed permutation once
;(function initPerm() {
  const p = new Uint8Array(256)
  for (let i = 0; i < 256; i++) p[i] = i
  // Fisher-Yates with fixed seed
  let seed = 42
  for (let i = 255; i > 0; i--) {
    seed = (seed * 16807 + 0) % 2147483647
    const j = seed % (i + 1)
    ;[p[i], p[j]] = [p[j], p[i]]
  }
  for (let i = 0; i < 512; i++) perm[i] = p[i & 255]
})()

function fade(t) {
  return t * t * t * (t * (t * 6 - 15) + 10)
}

function lerp(a, b, t) {
  return a + t * (b - a)
}

function dot2(g, x, y) {
  return g[0] * x + g[1] * y
}

function perlin2(x, y) {
  const xi = Math.floor(x) & 255
  const yi = Math.floor(y) & 255
  const xf = x - Math.floor(x)
  const yf = y - Math.floor(y)

  const u = fade(xf)
  const v = fade(yf)

  const g00 = grad2[perm[perm[xi] + yi] % 8]
  const g10 = grad2[perm[perm[xi + 1] + yi] % 8]
  const g01 = grad2[perm[perm[xi] + yi + 1] % 8]
  const g11 = grad2[perm[perm[xi + 1] + yi + 1] % 8]

  const n00 = dot2(g00, xf, yf)
  const n10 = dot2(g10, xf - 1, yf)
  const n01 = dot2(g01, xf, yf - 1)
  const n11 = dot2(g11, xf - 1, yf - 1)

  return lerp(lerp(n00, n10, u), lerp(n01, n11, u), v)
}

let cachedTexture = null

export function createPerlinNoiseTexture() {
  if (cachedTexture) return cachedTexture

  const data = new Float32Array(SIZE * SIZE)

  for (let y = 0; y < SIZE; y++) {
    for (let x = 0; x < SIZE; x++) {
      // Sample at a frequency that tiles nicely
      const nx = (x / SIZE) * 4
      const ny = (y / SIZE) * 4
      // Normalize from [-1,1] to [0,1]
      data[y * SIZE + x] = perlin2(nx, ny) * 0.5 + 0.5
    }
  }

  const texture = new THREE.DataTexture(
    data,
    SIZE,
    SIZE,
    THREE.RedFormat,
    THREE.FloatType,
  )
  texture.wrapS = THREE.RepeatWrapping
  texture.wrapT = THREE.RepeatWrapping
  texture.minFilter = THREE.LinearFilter
  texture.magFilter = THREE.LinearFilter
  texture.needsUpdate = true

  cachedTexture = texture
  return texture
}
